import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../../../services/navigation.service';

import { ClientService } from '../../../services/client.service';
import { SiteService } from '../../../services/site.service';
import { ToastService } from 'services/toast.service';

import { Equipment, Client, RootState, Site, Option, CommonGlobalService, dayjs, AbilityFilter, Ability, Skill, SkillFilter, Sector } from '@umanforge/common';
import { ModifyEquipment } from 'interfaces/api_equipment.interfaces';
import { EquipmentService } from 'services/equipment.service';
import { AbilityOfEquipmentLinked, SkillOfEquipmentLinked } from '@umanforge/common/dist/interfaces/equipment.interfaces';
import { AbilityService } from 'services/ability.service';
import { SkillService } from 'services/skill.service';
import { SectorService } from 'services/sector.service';

const toastService: ToastService = new ToastService( 'Details Equipment' );
const defaultChangedAbilities = { added: [], removed: [] };
const defaultChangedSkills = { added: [], removed: [] };

export function useDetailsEquipment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const dispatch = useDispatch();

  /*----------------------------- useState ------------------------------*/
  const [ equipment, setEquipment ] = useState<Equipment | null>( null );
  const [ sectorOfEquipment, setSectorOfEquipment ] = useState<Option<Sector> | undefined>();
  const [ siteOfSector, setSiteOfSector ] = useState<Option<Site> | undefined>();
  const [ clientOfSite, setClientOfSite ] = useState<Option<Client> | undefined>();

  const [ modifyEquipment, setModifyEquipment ] = useState<ModifyEquipment>( {} );

  const [ canPut, setCanPut ] = useState<boolean>( false );
  const [ hasChanged, setHasChanged ] = useState<boolean>( false );
  const [ isAdminOfSite, setIsAdminOfSite ] = useState<boolean>( false );
  const [ confirmDeleteModalIsOpen, setConfirmDeleteModalIsOpen ] = useState( false );


  const [ changedAbilities, setChangedAbilities ] = useState<{ added: AbilityOfEquipmentLinked[], removed: AbilityOfEquipmentLinked[] }>( defaultChangedAbilities );
  const [ defaultAbilities, setDefaultAbilities ] = useState<Option<AbilityOfEquipmentLinked>[]>( [] );
  const [ selectedAbilities, setSelectedAbilities ] = useState<Option<AbilityOfEquipmentLinked>[]>( [] );

  const [ changedSkills, setChangedSkills ] = useState<{ added: SkillOfEquipmentLinked[], removed: SkillOfEquipmentLinked[] }>( defaultChangedSkills );
  const [ defaultSkills, setDefaultSkills ] = useState<Option<SkillOfEquipmentLinked>[]>( [] );
  const [ selectedSkills, setSelectedSkills ] = useState<Option<SkillOfEquipmentLinked>[]>( [] );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    if ( me ) {
      refreshEquipment();
    }
  }, [ me ] );

  useEffect( () => {
    if ( equipment ) {
      checkCanPutEquipment();
    }
  }, [ modifyEquipment, changedAbilities, changedSkills ] );

  useEffect( () => {
    const items = {
      added: selectedAbilities
        .map( item => { return item.object; } )
        .filter( selectedAbility => {
          return (
            ( !defaultAbilities.find( currentAbility => { return ( currentAbility.object.id === selectedAbility.id ); } ) )
          );
        } ),
      removed: defaultAbilities
        .map( item => { return item.object; } )
        .filter( currentAbility => { return !selectedAbilities.find( selectedAbility => { return selectedAbility.object.id === currentAbility.id; } ); } )
    };
    setChangedAbilities( items );
  }, [ selectedAbilities ] );


  useEffect( () => {
    const items = {
      added: selectedSkills
        .map( item => { return item.object; } )
        .filter( selectedSkill => {
          return (
            ( !defaultSkills.find( currentSkill => { return ( currentSkill.object.id === selectedSkill.id ); } ) ) ||
            ( defaultSkills.find( currentSkill => { return ( currentSkill.object.id === selectedSkill.id ) && selectedSkill.value !== currentSkill.object.value; } ) )
          );
        } ),
      removed: defaultSkills
        .map( item => { return item.object; } )
        .filter( currentSkill => { return !selectedSkills.find( selectedSkill => { return selectedSkill.object.id === currentSkill.id; } ); } )
    };
    setChangedSkills( items );
  }, [ selectedSkills ] );


  /*------------------------- Requetes API User -------------------------*/
  const getAbilities = async ( filter: AbilityFilter ): Promise<{ total: number, list: Option<AbilityOfEquipmentLinked>[] }> => {
    if ( filter.filterName === undefined || filter.filterName === '' ) {
      delete filter.filterName;
    }
    if ( siteOfSector != null ) {
      filter.filterSites = [ siteOfSector.value ];
    }

    const abilityFilter = {
      filterName: filter.filterName,
      filterClients: filter.filterClients,
      filterSites: filter.filterSites
    };

    const result: Ability[] = ( await AbilityService.getAllowAbilities( abilityFilter as AbilityFilter, token, dispatch ) );
    return { total: result.length, list: CommonGlobalService.toOptions<AbilityOfEquipmentLinked>(
      result.map<AbilityOfEquipmentLinked>( ability => {
        return {
          id: ability.id,
          name: ability.name,
          disable: false,
          color: ability.color
        };
      } ) )
    };
  };

  const getSkills = async ( filter: SkillFilter ): Promise<{ total: number, list: Option<SkillOfEquipmentLinked>[] }> => {
    if ( filter.filterName === undefined || filter.filterName === '' ) {
      delete filter.filterName;
    }
    if ( siteOfSector != null ) {
      filter.filterSites = [ siteOfSector.value ];
    }

    const skillFilter = {
      filterName: filter.filterName,
      filterClients: filter.filterClients,
      filterSites: filter.filterSites
    };

    const result: Skill[] = ( await SkillService.getAllowSkills( skillFilter as SkillFilter, token, dispatch ) );
    return { total: result.length, list: CommonGlobalService.toOptions<SkillOfEquipmentLinked>(
      result.map<SkillOfEquipmentLinked>( skill => {
        return {
          id: skill.id,
          name: skill.name,
          value: skill.value ?? 1,
          disable: false,
          color: skill.color
        };
      } ) )
    };
  };

  const refreshEquipment = async () => {
    setEquipment( null );
    setIsLoading( true );
    setModifyEquipment( {} );
    if ( params.equipmentId ) {
      const refreshedEquipment = await EquipmentService.getEquipment( params.equipmentId, token, dispatch, () => { NavigationService.toEquipments( navigate, false ); } );
      const refreshedSector = await SectorService.getSector( refreshedEquipment.sectorId, token, dispatch, () => { NavigationService.toEquipments( navigate, false ); } );
      const refreshedSite = await SiteService.getSite( refreshedSector.siteId, token, dispatch, () => { NavigationService.toEquipments( navigate, false ); } );
      const refreshedClient = await ClientService.getClient( refreshedSite.clientId, token, dispatch, () => { NavigationService.toEquipments( navigate, false ); } );
      const isAdmin: boolean = ( me != null && ( CommonGlobalService.isAdminOfSite( me, refreshedSite.id ) || CommonGlobalService.isAdminOfClient( me, refreshedClient.id ) ) );
      setIsAdminOfSite( isAdmin );

      const abilities = await EquipmentService.getAbilitiesOfEquipment( params.equipmentId, token, dispatch, () => { NavigationService.toEquipments( navigate, false ); } );
      const equipmentAbilities: Option<AbilityOfEquipmentLinked>[] = CommonGlobalService.toOptions(
        abilities, isAdmin, !isAdmin,
        ( item1, item2 ) => {
          return item1.name.toUpperCase() < item2.name.toUpperCase() ? -1 : 1;
        }
      );

      const skills = await EquipmentService.getSkillsOfEquipment( params.equipmentId, token, dispatch, () => { NavigationService.toEquipments( navigate, false ); } );
      const equipmentSkills: Option<SkillOfEquipmentLinked>[] = CommonGlobalService.toOptions(
        skills, isAdmin, !isAdmin,
        ( item1, item2 ) => {
          return item1.name.toUpperCase() < item2.name.toUpperCase() ? -1 : 1;
        }
      );

      setSectorOfEquipment( CommonGlobalService.toOptions( [ refreshedSector ] )[ 0 ] );
      setSiteOfSector( CommonGlobalService.toOptions( [ refreshedSite ] )[ 0 ] );
      setClientOfSite( CommonGlobalService.toOptions( [ refreshedClient ] )[ 0 ] );
      setEquipment( refreshedEquipment );
      setDefaultAbilities( structuredClone( equipmentAbilities ) );
      setSelectedAbilities( structuredClone( equipmentAbilities ) );
      setDefaultSkills( structuredClone( equipmentSkills ) );
      setSelectedSkills( structuredClone( equipmentSkills ) );
      setIsLoading( false );
    }
  };

  const deleteEquipment = (): Promise<boolean> => {
    return new Promise ( async resolve => {
      setIsLoading( true );
      let isDeleted = false;
      if ( params.equipmentId ) {
        isDeleted = await EquipmentService.deleteEquipment( params.equipmentId, token, dispatch, refreshEquipment, refreshEquipment );
      }
      if ( isDeleted ) {
        toastService.showInfo( t( 'the_equipment_has_been_delete' ) );
        NavigationService.toEquipments( navigate, false );
      }
      setIsLoading( false );
      resolve( isDeleted );
    } );
  };

  const putEquipment = (): Promise<boolean> => {
    return new Promise ( async resolve => {
      let isUpdated = false;
      const isEquipmentNameChange: boolean =  modifyEquipment.name != null && modifyEquipment.name != '' && modifyEquipment.name != equipment?.name;
      const isEquipmentDescriptionChange: boolean =  modifyEquipment.description != null && modifyEquipment.description != '' && modifyEquipment.description != equipment?.description;
      const isEquipmentExpirationDateChange: boolean =
        modifyEquipment.expirationDate != null &&
        modifyEquipment.expirationDate != equipment?.expirationDate &&
        dayjs( modifyEquipment.expirationDate ).isAfter( dayjs().add( -1, 'day' ).startOf( 'day' ), 'date' );

      if ( params.equipmentId && ( isEquipmentNameChange || isEquipmentDescriptionChange || isEquipmentExpirationDateChange ) ) {
        isUpdated = await EquipmentService.putEquipment( params.equipmentId, modifyEquipment, token, dispatch, refreshEquipment, refreshEquipment );
      }
      resolve( isUpdated );
    } );
  };


  const addAbilityToEquipment = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( equipment?.id && changedAbilities.added.length > 0 ) {
        const body = { abilitiesId: changedAbilities.added.map( ability => { return ability.id; } ) };
        isUpdated = await EquipmentService.postAbilitiesToEquipment( equipment?.id, body, token, dispatch, refreshEquipment, refreshEquipment );
      }
      resolve( isUpdated );
    } );
  };

  const removedAbilityToEquipment = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( equipment?.id && changedAbilities.removed.length > 0 ) {
        const body = { abilitiesId: changedAbilities.removed.map( ability => { return ability.id; } ) };
        isUpdated = await EquipmentService.deleteAbilitiesToEquipment( equipment?.id, body, token, dispatch, refreshEquipment, refreshEquipment );
      }
      resolve( isUpdated );
    } );
  };


  const addSkillToEquipment = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( equipment?.id && changedSkills.added.length > 0 ) {
        const body = { skills: changedSkills.added.map( skill => { return { id: skill.id, value: skill.value }; } ) };
        isUpdated = await EquipmentService.postSkillsToEquipment( equipment?.id, body, token, dispatch, refreshEquipment, refreshEquipment );
      }
      resolve( isUpdated );
    } );
  };

  const removedSkillToEquipment = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( equipment?.id && changedSkills.removed.length > 0 ) {
        const body = { skillsId: changedSkills.removed.map( skill => { return skill.id; } ) };
        isUpdated = await EquipmentService.deleteSkillsToEquipment( equipment?.id, body, token, dispatch, refreshEquipment, refreshEquipment );
      }
      resolve( isUpdated );
    } );
  };

  /*---------------------------- Functions ------------------------------*/
  const onSaveClick = async () => {
    setIsLoading( true );
    let isEquipmentChanged = false;
    if ( equipment ) {
      const isPutEquipment = await putEquipment();
      const isAddAbilityToEquipment = await addAbilityToEquipment();
      const isRemovedAbilityToEquipment = await removedAbilityToEquipment();
      const isAddSkillToEquipment = await addSkillToEquipment();
      const isRemovedSkillToEquipment = await removedSkillToEquipment();

      isEquipmentChanged = isPutEquipment ||
        isAddAbilityToEquipment ||
        isRemovedAbilityToEquipment ||
        isAddSkillToEquipment ||
        isRemovedSkillToEquipment;
    }
    if ( isEquipmentChanged ) {
      toastService.showInfo( t( 'the_equipment_has_been_modify' ) );
      await refreshEquipment();
      setCanPut( false );
    }
    setIsLoading( false );
  };

  const checkCanPutEquipment = () => {
    const requirement = ( modifyEquipment.name != '' && modifyEquipment.description != '' && dayjs( modifyEquipment.expirationDate ?? equipment?.expirationDate ).isAfter( dayjs().add( -1, 'day' ).startOf( 'day' ), 'date' ) );
    const isEquipmentNameChange: boolean =  modifyEquipment.name != null && modifyEquipment.name != equipment?.name;
    const isEquipmentDescriptionChange: boolean =  modifyEquipment.description != null && modifyEquipment.description != equipment?.description;
    const isAbilitiesChange: boolean = changedAbilities.added.length > 0 || changedAbilities.removed.length > 0;
    const isSkillsChange: boolean = changedSkills.added.length > 0 || changedSkills.removed.length > 0;
    const isEquipmentExpirationDateChange: boolean =
      modifyEquipment.expirationDate != null &&
      modifyEquipment.expirationDate != equipment?.expirationDate;

    setCanPut(
      requirement && (
        isEquipmentNameChange ||
        isEquipmentDescriptionChange ||
        isEquipmentExpirationDateChange ||
        isAbilitiesChange ||
        isSkillsChange
      )
    );
    setHasChanged(
      isEquipmentNameChange ||
      isEquipmentDescriptionChange ||
      isEquipmentExpirationDateChange ||
      isAbilitiesChange ||
      isSkillsChange
    );
  };

  return {
    equipment, setEquipment,
    sectorOfEquipment, setSectorOfEquipment,
    siteOfSector, setSiteOfSector,
    clientOfSite, setClientOfSite,
    modifyEquipment, setModifyEquipment,
    canPut, setCanPut,
    hasChanged, setHasChanged,
    isAdminOfSite, setIsAdminOfSite,
    confirmDeleteModalIsOpen, setConfirmDeleteModalIsOpen,
    isLoading, setIsLoading,
    deleteEquipment,
    onSaveClick,

    selectedAbilities, setSelectedAbilities,
    getAbilities,

    selectedSkills, setSelectedSkills,
    getSkills
  };
}